import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Loader2,
  Shield,
  User,
  Mail,
  Phone,
  CheckCircle2,
  Search,
  ChevronLeft,
  CalendarIcon,
  Clock,
  Video,
  MapPin,
  Building2,
  Tag,
} from "lucide-react";
import { format } from "date-fns";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { PatientSearchDropdown } from "@/components/PatientSearchDropdown";
import { appointmentsAPI } from "@/lib/appointments-api";
import {
  getApiErrorCode,
  getApiErrorMessage,
  getApiErrorStatus,
} from "@/lib/api";
import { useServiceModes } from "@/hooks/useServiceModes";
import type { User as PatientUser } from "@/types";
import type {
  Category,
  CheckoutPricing,
  Service,
  Doctor,
} from "@/types/appointments";
import type { Clinic, ServiceModesResponse } from "@/types/clinics";
import { cn } from "@/lib/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AppointmentPaymentTabs,
  type AppointmentPaymentTab,
} from "@/components/appointments/AppointmentPaymentTabs";
import { ReceptionPaymentLinkPanel } from "@/components/settings/ReceptionPaymentLinkPanel";
import { queryKeys } from "@/lib/query-keys";

type ServiceMode = ServiceModesResponse["modes"][number];
type StaffBackTarget = "service" | "mode" | "clinic";
type ClinicBackTarget = "service" | "mode";

// Form validation schema
const appointmentSchema = z
  .object({
    patientId: z.string().min(1, "Patient selection is required"),
    clientName: z.string().min(1, "Client name is required"),
    email: z
      .string()
      .email("Invalid email address")
      .optional()
      .or(z.literal("")),
    phoneNumber: z.string().min(10, "Valid phone number is required"),
    categoryId: z.string().min(1, "Category selection is required"),
    serviceId: z.string().min(1, "Service selection is required"),
    mode: z.string().min(1, "Consultation type selection is required"),
    clinicId: z.string().nullable(),
    doctorId: z.string().min(1, "Doctor selection is required"),
    appointmentDate: z.date({ required_error: "Date selection is required" }),
    timeSlot: z.string().min(1, "Time slot selection is required"),
    promoCode: z.string().trim().optional().default(""),
    partOfProgram: z.boolean().optional().default(false),
  })
  .superRefine((values, ctx) => {
    if (values.mode === "clinic" && !values.clinicId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Clinic selection is required for in-person consultations",
        path: ["clinicId"],
      });
    }
  });

type AppointmentFormValues = z.infer<typeof appointmentSchema>;

export const AddAppointmentForm = () => {
  const { toast } = useToast();
  const { hasPermission } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientUser | null>(
    null
  );
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const resumedCollectionAppointmentId = searchParams.get("collection");
  const [currentStep, setCurrentStep] = useState<
    | "patient"
    | "service"
    | "mode"
    | "clinic"
    | "staff"
    | "datetime"
    | "collection"
  >(resumedCollectionAppointmentId ? "collection" : "patient");
  const [collectionAppointmentId, setCollectionAppointmentId] = useState<
    string | null
  >(resumedCollectionAppointmentId);
  const [collectionAmountPaise, setCollectionAmountPaise] = useState<
    number | undefined
  >();
  const [collectionCurrency, setCollectionCurrency] = useState("INR");
  const [services, setServices] = useState<Service[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<string | null>(null);
  const [doctorSearchQuery, setDoctorSearchQuery] = useState("");
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [baseQuote, setBaseQuote] = useState<CheckoutPricing | null>(null);
  const [promoQuote, setPromoQuote] = useState<CheckoutPricing | null>(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [checkoutBlockingError, setCheckoutBlockingError] = useState<
    string | null
  >(null);

  // Mode / clinic (multi-location booking) state
  const [mode, setMode] = useState<string>("teleconsult");
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [availableModes, setAvailableModes] = useState<ServiceMode[]>([]);
  const [availableClinics, setAvailableClinics] = useState<Clinic[]>([]);
  const [staffBackTarget, setStaffBackTarget] =
    useState<StaffBackTarget>("service");
  const [clinicBackTarget, setClinicBackTarget] =
    useState<ClinicBackTarget>("mode");
  // Guards the modes-resolution effect against re-running for a service
  // whose modes have already been processed (e.g. cached refetches).
  const processedModesServiceRef = useRef<string | null>(null);

  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      patientId: "",
      clientName: "",
      email: "",
      phoneNumber: "",
      categoryId: "",
      serviceId: "",
      mode: "teleconsult",
      clinicId: null,
      doctorId: "",
      appointmentDate: undefined,
      timeSlot: "",
      promoCode: "",
      partOfProgram: false,
    },
  });
  const promoCodeValue = form.watch("promoCode");
  const partOfProgram = form.watch("partOfProgram");
  const normalizedPromoCode = promoCodeValue.trim();
  const activeQuote = promoQuote ?? baseQuote;
  const displayedQuote =
    partOfProgram && activeQuote
      ? {
          ...activeQuote,
          discount_paise: activeQuote.subtotal_paise,
          payable_paise: 0,
          promo_code: null,
        }
      : activeQuote;
  const promoNeedsApply =
    Boolean(normalizedPromoCode) &&
    promoQuote?.promo_code?.toLowerCase() !== normalizedPromoCode.toLowerCase();

  const resetPromoState = () => {
    setBaseQuote(null);
    setPromoQuote(null);
    form.clearErrors("promoCode");
  };

  // Fetch categories when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      fetchCategories();
    }
  }, [selectedPatient]);

  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const data = await appointmentsAPI.getCategories();
      // Sort by priority (ascending)
      const sortedCategories = data
        .filter((cat) => cat.is_active)
        .sort((a, b) => a.priority - b.priority);
      setCategories(sortedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast({
        title: "Error",
        description: "Failed to load categories",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Handle patient selection
  const handlePatientSelect = (patient: PatientUser | null) => {
    resetPromoState();
    setSelectedPatient(patient);

    if (patient) {
      form.setValue("patientId", patient.id);
      form.setValue("clientName", `${patient.first_name} ${patient.last_name}`);
      form.setValue("email", patient.email || "");
      form.setValue("phoneNumber", patient.phone_number);
      form.clearErrors();
    } else {
      form.reset();
      setSelectedCategory(null);
      setCategories([]);
    }
  };

  // Handle category selection
  const handleCategorySelect = async (categoryId: string) => {
    setSelectedCategory(categoryId);
    form.setValue("categoryId", categoryId);
    form.clearErrors("categoryId");

    // Fetch services for selected category
    await fetchServices(categoryId);
  };

  // Fetch services by category
  const fetchServices = async (categoryId: string) => {
    setIsLoadingServices(true);
    try {
      const data = await appointmentsAPI.getServicesByCategory(categoryId);
      // Sort by order and filter active
      const sortedServices = data
        .filter((service) => service.is_active)
        .sort((a, b) => a.order - b.order);
      setServices(sortedServices);
      // Move to service selection step
      setCurrentStep("service");
    } catch (error) {
      console.error("Error fetching services:", error);
      setSelectedCategory(null);
      form.setValue("categoryId", "");
      toast({
        title: "Error",
        description: "Failed to load services. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingServices(false);
    }
  };

  // Reset all mode/clinic selection state (used whenever we head back to
  // pick a different service, or after a successful booking).
  const resetModeAndClinicState = () => {
    setMode("teleconsult");
    form.setValue("mode", "teleconsult");
    setClinicId(null);
    form.setValue("clinicId", null);
    setSelectedClinic(null);
    setAvailableModes([]);
    setAvailableClinics([]);
    setStaffBackTarget("service");
    setClinicBackTarget("mode");
    processedModesServiceRef.current = null;
  };

  const resetBookingForm = () => {
    form.reset({
      patientId: "",
      clientName: "",
      email: "",
      phoneNumber: "",
      categoryId: "",
      serviceId: "",
      mode: "teleconsult",
      clinicId: null,
      doctorId: "",
      appointmentDate: undefined,
      timeSlot: "",
      promoCode: "",
      partOfProgram: false,
    });
    setCurrentStep("patient");
    setSelectedPatient(null);
    setSelectedCategory(null);
    setSelectedService(null);
    setSelectedDoctor(null);
    setSelectedDate(undefined);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setServiceSearchQuery("");
    setDoctorSearchQuery("");
    setCollectionAppointmentId(null);
    setSearchParams({}, { replace: true });
    setCollectionAmountPaise(undefined);
    setCollectionCurrency("INR");
    setBaseQuote(null);
    setPromoQuote(null);
    setCheckoutBlockingError(null);
    resetModeAndClinicState();
  };

  const continueCollectionLater = () => {
    setCollectionAppointmentId(null);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.all,
    });
    navigate("/settings/appointments/awaiting-payment", { replace: true });
  };

  const handlePaymentTabChange = (tab: AppointmentPaymentTab) => {
    if (tab === "awaiting-payment" && collectionAppointmentId) {
      continueCollectionLater();
      return;
    }
    navigate(`/settings/appointments/${tab}`);
  };

  // Handle service selection — kicks off mode resolution via useServiceModes
  const handleServiceSelect = (serviceId: string) => {
    resetPromoState();
    setSelectedService(serviceId);
    form.setValue("serviceId", serviceId);
    form.clearErrors("serviceId");

    // A new service may have a different mode/clinic configuration —
    // clear any prior selection before resolving the new one.
    resetModeAndClinicState();
    setSelectedDoctor(null);
    setDoctors([]);

    setCurrentStep("mode");
  };

  // Fetch available booking modes for the selected service
  const {
    data: serviceModesData,
    isLoading: isLoadingModes,
    isError: isModesError,
  } = useServiceModes(
    selectedService,
    currentStep === "mode" && !!selectedService
  );

  // Fetch doctors for the resolved mode/clinic combination
  const fetchDoctorsForSelection = async (
    modeValue: string,
    clinicIdValue: string | null,
    backTarget: StaffBackTarget
  ) => {
    if (!selectedService) return;
    setIsLoadingDoctors(true);
    try {
      const data = await appointmentsAPI.getDoctorsByService(selectedService, {
        mode: modeValue,
        clinic_id: clinicIdValue ?? undefined,
      });
      setDoctors(data);
      setStaffBackTarget(backTarget);
      setCurrentStep("staff");
    } catch (error) {
      console.error("Error fetching doctors:", error);
      toast({
        title: "Error",
        description: "Failed to load staff members",
        variant: "destructive",
      });
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Handle clinic selection (either auto-selected or picked from the list)
  const selectClinic = async (clinic: Clinic, backTarget: StaffBackTarget) => {
    setClinicId(clinic.id);
    form.setValue("clinicId", clinic.id);
    form.clearErrors("clinicId");
    setSelectedClinic(clinic);
    await fetchDoctorsForSelection("clinic", clinic.id, backTarget);
  };

  // Handle clinic picked from the clinic picker step
  const handleClinicPick = (clinic: Clinic) => {
    void selectClinic(clinic, "clinic");
  };

  // Resolve a chosen mode — either auto-resolved (single mode available) or
  // explicitly picked by the user from the mode picker.
  const selectMode = async (
    modeEntry: ServiceMode,
    source: "auto" | "picker"
  ) => {
    setMode(modeEntry.mode);
    form.setValue("mode", modeEntry.mode);
    form.clearErrors("mode");

    if (modeEntry.mode === "clinic") {
      const clinics = modeEntry.clinics ?? [];
      setAvailableClinics(clinics);

      if (clinics.length === 0) {
        toast({
          title: "Error",
          description: "No clinics are available for in-person consultation",
          variant: "destructive",
        });
        setCurrentStep(source === "auto" ? "service" : "mode");
        return;
      }

      setClinicBackTarget(source === "auto" ? "service" : "mode");
      setCurrentStep("clinic");
      return;
    }

    setClinicId(null);
    form.setValue("clinicId", null);
    setSelectedClinic(null);
    await fetchDoctorsForSelection(
      modeEntry.mode,
      null,
      source === "auto" ? "service" : "mode"
    );
  };

  const handleModePick = (modeEntry: ServiceMode) => {
    void selectMode(modeEntry, "picker");
  };

  // Resolve booking modes once they've loaded for the selected service
  useEffect(() => {
    if (
      currentStep !== "mode" ||
      !selectedService ||
      !serviceModesData ||
      processedModesServiceRef.current === selectedService
    ) {
      return;
    }

    processedModesServiceRef.current = selectedService;
    const modes = serviceModesData.modes ?? [];
    setAvailableModes(modes);

    if (modes.length === 0) {
      toast({
        title: "Error",
        description: "No booking modes are available for this service",
        variant: "destructive",
      });
      setCurrentStep("service");
      return;
    }

    if (modes.length === 1) {
      void selectMode(modes[0], "auto");
    }
    // Otherwise stay on the "mode" step and let the user pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceModesData, currentStep, selectedService]);

  // Surface mode-fetch failures and bail back to the service list
  useEffect(() => {
    if (isModesError && currentStep === "mode") {
      toast({
        title: "Error",
        description: "Failed to load consultation modes for this service",
        variant: "destructive",
      });
      setCurrentStep("service");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModesError, currentStep]);

  // Handle doctor selection
  const handleDoctorSelect = (doctorId: string) => {
    resetPromoState();
    setSelectedDoctor(doctorId);
    form.setValue("doctorId", doctorId);
    form.clearErrors("doctorId");
    // Move to datetime step after doctor selection
    setCurrentStep("datetime");
  };

  // Handle back to patient/category step
  const handleBackToPatientStep = () => {
    setCurrentStep("patient");
    setSelectedService(null);
    setServices([]);
    setSelectedDoctor(null);
    setDoctors([]);
    setServiceSearchQuery("");
    setDoctorSearchQuery("");
    resetModeAndClinicState();
  };

  // Handle back to service step (from the mode step)
  const handleBackToServiceStep = () => {
    setCurrentStep("service");
    setSelectedService(null);
    form.setValue("serviceId", "");
    setSelectedDoctor(null);
    setDoctors([]);
    setServiceSearchQuery("");
    resetModeAndClinicState();
  };

  // Handle back from the clinic picker step
  const handleBackFromClinicStep = () => {
    setClinicId(null);
    form.setValue("clinicId", null);
    setSelectedClinic(null);

    if (clinicBackTarget === "service") {
      handleBackToServiceStep();
      return;
    }

    setCurrentStep("mode");
  };

  // Handle back from the staff (doctor) step — target depends on how we
  // arrived here (mode was auto-skipped, chosen from the picker, or a
  // clinic was chosen from the clinic picker).
  const handleBackFromStaffStep = () => {
    setSelectedDoctor(null);
    setDoctors([]);
    setDoctorSearchQuery("");
    setSelectedDate(undefined);
    setSelectedSlot(null);
    setAvailableSlots([]);

    if (staffBackTarget === "service") {
      handleBackToServiceStep();
      return;
    }

    setClinicId(null);
    form.setValue("clinicId", null);
    setSelectedClinic(null);

    setCurrentStep(staffBackTarget === "mode" ? "mode" : "clinic");
  };

  // Handle back to staff step (from datetime step)
  const handleBackToStaffStep = () => {
    setCurrentStep("staff");
    setSelectedDate(undefined);
    setSelectedSlot(null);
    setAvailableSlots([]);
  };

  // Fetch available slots when date is selected
  const handleDateSelect = async (date: Date | undefined) => {
    if (!date || !selectedService || !selectedDoctor) return;

    setSelectedDate(date);
    form.setValue("appointmentDate", date);
    form.clearErrors("appointmentDate");
    setSelectedSlot(null);
    form.setValue("timeSlot", "");

    // Fetch slots for the selected date
    setIsLoadingSlots(true);
    try {
      const formattedDate = format(date, "yyyy-MM-dd");
      const data = await appointmentsAPI.getAvailableSlots(
        selectedService,
        selectedDoctor,
        formattedDate,
        { mode, clinic_id: clinicId ?? undefined }
      );
      setAvailableSlots(data.available_slots);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast({
        title: "Error",
        description: "Failed to load available slots",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // Handle slot selection
  const loadCheckoutQuote = async (promoCode: string) => {
    if (!selectedPatient || !selectedService || !selectedDoctor) return;
    setIsApplyingPromo(true);
    form.clearErrors("promoCode");
    try {
      if (promoCode && !baseQuote) {
        const baseline = await appointmentsAPI.getConsoleCheckoutQuote(
          selectedService,
          selectedDoctor,
          selectedPatient.id
        );
        setBaseQuote(baseline.pricing);
      }
      const response = await appointmentsAPI.getConsoleCheckoutQuote(
        selectedService,
        selectedDoctor,
        selectedPatient.id,
        promoCode
      );
      if (promoCode) {
        setPromoQuote(response.pricing);
      } else {
        setBaseQuote(response.pricing);
        setPromoQuote(null);
      }
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        "This promo code is invalid or no longer available."
      );
      setPromoQuote(null);
      form.setError("promoCode", { type: "server", message });
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const handleSlotSelect = (slot: string) => {
    setSelectedSlot(slot);
    form.setValue("timeSlot", slot);
    form.clearErrors("timeSlot");
    void loadCheckoutQuote(normalizedPromoCode);
  };

  // Filter services based on search (flat list — no mode tabs)
  const getFilteredServices = () => {
    if (!serviceSearchQuery.trim()) {
      return services;
    }

    const query = serviceSearchQuery.toLowerCase();
    return services.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.description.toLowerCase().includes(query)
    );
  };

  // Filter doctors based on search
  const getFilteredDoctors = () => {
    if (!doctorSearchQuery.trim()) {
      return doctors;
    }

    const query = doctorSearchQuery.toLowerCase();
    return doctors.filter(
      (d) =>
        d.doctor_name.toLowerCase().includes(query) ||
        d.doctor_designation.toLowerCase().includes(query)
    );
  };

  const handleSubmit = async (values: AppointmentFormValues) => {
    const submittedPromoCode = values.promoCode.trim();
    if (
      submittedPromoCode &&
      promoQuote?.promo_code?.toLowerCase() !== submittedPromoCode.toLowerCase()
    ) {
      const message =
        "Apply this promo code before scheduling the appointment.";
      form.setError("promoCode", { type: "manual", message });
      return;
    }
    setIsSubmitting(true);
    try {
      if (
        !values.serviceId ||
        !values.doctorId ||
        !values.appointmentDate ||
        !values.timeSlot
      ) {
        throw new Error("Missing required fields");
      }

      const appointmentData = {
        date: format(values.appointmentDate, "yyyy-MM-dd"),
        start_time: values.timeSlot,
        patient_id: values.patientId,
        part_of_program: values.partOfProgram ? "true" : "false",
        mode: values.mode,
        clinic_id:
          values.mode === "clinic" && values.clinicId
            ? values.clinicId
            : undefined,
        promo_code: submittedPromoCode || undefined,
      };

      const response = await appointmentsAPI.createAppointment(
        values.serviceId,
        values.doctorId,
        appointmentData
      );

      if (response.checkout?.action === "payment_required") {
        setSearchParams(
          { collection: response.appointment_id },
          { replace: true }
        );
        setCollectionAppointmentId(response.appointment_id);
        setCollectionAmountPaise(response.checkout.amount_paise);
        setCollectionCurrency(response.checkout.currency);
        setCurrentStep("collection");
        return;
      }

      toast({
        title: "Success",
        description: `Appointment created successfully! ID: ${response.appointment_id}`,
      });

      resetBookingForm();
    } catch (error: unknown) {
      if (getApiErrorCode(error) === "promo_not_available") {
        const message = getApiErrorMessage(
          error,
          "This promo code is no longer available."
        );
        setPromoQuote(null);
        form.setError("promoCode", { type: "server", message });
        setCheckoutBlockingError(message);
        return;
      }
      const status = getApiErrorStatus(error);
      const isPermissionError = status === 403;
      const message = getApiErrorMessage(
        error,
        "Failed to create appointment. Please try again."
      );
      // A timed-out booking is not a failed booking: the server sends the
      // patient's SMS, WhatsApp and Slack inside the request and may finish
      // after we stop waiting. Never invite a blind retry here.
      const isTimeout = status === 0 && /timeout/i.test(message);
      console.error("Error creating appointment:", error);
      toast({
        title: isPermissionError
          ? "Access Denied"
          : isTimeout
            ? "Still processing"
            : "Error",
        description: isPermissionError
          ? "Your permissions no longer allow appointment creation."
          : isTimeout
            ? "This is taking longer than expected and the appointment may still have been created. Check the appointments list before booking again."
            : message,
        variant: isTimeout ? "default" : "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_APPOINTMENTS)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Shield className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Permission Required
            </h3>
            <p className="text-muted-foreground mb-4">
              You need appointment-management permission to access this feature.
            </p>
            <p className="text-sm text-muted-foreground">
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 pb-2">
      <AppointmentPaymentTabs
        activeTab="add-appointment"
        onTabChange={handlePaymentTabChange}
      />
      <Card className="h-full flex flex-col">
        <CardHeader className="shrink-0">
          <div className="flex items-center gap-3">
            {currentStep === "patient" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/settings/appointments/calendar")}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {currentStep === "service" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackToPatientStep}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {currentStep === "mode" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackToServiceStep}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {currentStep === "clinic" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackFromClinicStep}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {currentStep === "staff" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackFromStaffStep}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {currentStep === "datetime" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackToStaffStep}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            <CardTitle>
              {currentStep === "patient"
                ? "Add New Appointment"
                : currentStep === "service"
                  ? "Select a Service"
                  : currentStep === "mode"
                    ? "Choose Consultation Type"
                    : currentStep === "clinic"
                      ? "Select a Clinic"
                      : currentStep === "staff"
                        ? "Schedule An Appointment"
                        : currentStep === "collection"
                          ? "Collect appointment payment"
                          : "Select Date & Time"}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto">
          {currentStep === "collection" && collectionAppointmentId ? (
            <ReceptionPaymentLinkPanel
              appointmentId={collectionAppointmentId}
              amountPaise={collectionAmountPaise}
              currency={collectionCurrency}
              onFinished={() => {
                toast({
                  title: "Appointment confirmed",
                  description: "Payment was confirmed successfully.",
                });
                resetBookingForm();
              }}
              onRestart={resetBookingForm}
              onContinueLater={continueCollectionLater}
            />
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-6 max-w-5xl mx-auto"
              >
                {currentStep === "patient" ? (
                  <>
                    {/* Patient Search */}
                    <FormField
                      control={form.control}
                      name="patientId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Select Patient for this appointment *
                          </FormLabel>
                          <FormControl>
                            <PatientSearchDropdown
                              selectedPatient={selectedPatient}
                              onPatientSelect={handlePatientSelect}
                              placeholder="Search by typing patient Name"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {selectedPatient && (
                      <>
                        <div className="border-t pt-6" />

                        {/* Client Name - Read Only */}
                        <FormField
                          control={form.control}
                          name="clientName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Client Name
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  readOnly
                                  className="bg-muted/30"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Email - Read Only */}
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Email Id
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  readOnly
                                  className="bg-muted/30"
                                  placeholder="No email provided"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Phone Number - Read Only */}
                        <FormField
                          control={form.control}
                          name="phoneNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Phone Number
                              </FormLabel>
                              <FormControl>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    disabled
                                    className="bg-muted/30"
                                  >
                                    🇮🇳 +91 IN
                                  </Button>
                                  <Input
                                    {...field}
                                    readOnly
                                    className="flex-1 bg-muted/30"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="border-t pt-6" />

                        {/* Category Selection */}
                        <FormField
                          control={form.control}
                          name="categoryId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Select Category *</FormLabel>
                              <FormControl>
                                {isLoadingCategories ? (
                                  <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    <span className="ml-2 text-sm text-muted-foreground">
                                      Loading categories...
                                    </span>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {categories.map((category) => (
                                      <button
                                        key={category.id}
                                        type="button"
                                        onClick={() =>
                                          handleCategorySelect(category.id)
                                        }
                                        disabled={isLoadingServices}
                                        className={cn(
                                          "relative p-4 border-2 rounded-lg transition-all hover:shadow-md",
                                          selectedCategory === category.id
                                            ? "border-primary bg-primary/10"
                                            : "border-border hover:border-border",
                                          isLoadingServices &&
                                            "opacity-50 cursor-not-allowed"
                                        )}
                                      >
                                        {selectedCategory === category.id &&
                                          !isLoadingServices && (
                                            <CheckCircle2 className="absolute top-2 right-2 h-5 w-5 text-primary" />
                                          )}
                                        {selectedCategory === category.id &&
                                          isLoadingServices && (
                                            <Loader2 className="absolute top-2 right-2 h-5 w-5 text-primary animate-spin" />
                                          )}
                                        <div className="text-center">
                                          <h3 className="font-semibold text-foreground">
                                            {category.name}
                                          </h3>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}
                  </>
                ) : currentStep === "service" ? (
                  <>
                    {/* Service Selection Step — flat list, no mode tabs */}
                    {/* Search Bar */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                      <Input
                        placeholder="Search"
                        value={serviceSearchQuery}
                        onChange={(e) => setServiceSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {isLoadingServices ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {getFilteredServices().map((service) => (
                          <button
                            key={service.id}
                            type="button"
                            onClick={() => handleServiceSelect(service.id)}
                            className={cn(
                              "w-full p-4 border-2 rounded-lg transition-all hover:shadow-md text-left",
                              selectedService === service.id
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-border"
                            )}
                          >
                            <div className="flex gap-4">
                              {service.image && (
                                <img
                                  src={service.image}
                                  alt={service.name}
                                  className="w-20 h-20 object-cover rounded-lg shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-foreground">
                                  {service.name}
                                </h3>
                                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                  <span>₹{service.cost}</span>
                                  <span>•</span>
                                  <span>{service.event_duration} min</span>
                                </div>
                                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                  {service.description}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                        {getFilteredServices().length === 0 && (
                          <div className="text-center py-12 text-muted-foreground">
                            No services available
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : currentStep === "mode" ? (
                  <>
                    {/* Consultation Type (Mode) Selection Step */}
                    <FormField
                      control={form.control}
                      name="mode"
                      render={() => (
                        <FormItem>
                          <FormLabel>Choose Consultation Type *</FormLabel>
                          <FormControl>
                            {isLoadingModes || availableModes.length <= 1 ? (
                              <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {availableModes.map((modeEntry) => (
                                  <button
                                    key={modeEntry.mode}
                                    type="button"
                                    onClick={() => handleModePick(modeEntry)}
                                    className="w-full p-4 border-2 rounded-lg transition-all hover:shadow-md text-left border-border hover:border-border"
                                  >
                                    <div className="flex items-center gap-4">
                                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                        {modeEntry.mode === "clinic" ? (
                                          <MapPin className="h-5 w-5" />
                                        ) : (
                                          <Video className="h-5 w-5" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-foreground">
                                          {modeEntry.label}
                                        </h3>
                                        <p className="text-sm text-muted-foreground mt-1">
                                          {modeEntry.mode === "clinic"
                                            ? "Visit a clinic in person for this consultation"
                                            : "Consult with the doctor over a video call"}
                                        </p>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : currentStep === "clinic" ? (
                  <>
                    {/* Clinic Selection Step */}
                    <FormField
                      control={form.control}
                      name="clinicId"
                      render={() => (
                        <FormItem>
                          <FormLabel>Select a Clinic *</FormLabel>
                          <FormControl>
                            <div className="space-y-4">
                              {availableClinics.map((clinic) => (
                                <button
                                  key={clinic.id}
                                  type="button"
                                  onClick={() => handleClinicPick(clinic)}
                                  className="w-full p-4 border-2 rounded-lg transition-all hover:shadow-md text-left border-border hover:border-border"
                                >
                                  <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                      <Building2 className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3 className="font-semibold text-foreground">
                                        {clinic.name}
                                      </h3>
                                      <p className="text-sm text-muted-foreground mt-1">
                                        {clinic.city}
                                      </p>
                                      {(clinic.address_line1 ||
                                        clinic.address_line2) && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                          {[
                                            clinic.address_line1,
                                            clinic.address_line2,
                                          ]
                                            .filter(Boolean)
                                            .join(", ")}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              ))}
                              {availableClinics.length === 0 && (
                                <div className="text-center py-12 text-muted-foreground">
                                  No clinics available
                                </div>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : currentStep === "staff" ? (
                  <>
                    {/* Staff Selection Step */}
                    {/* Patient Info Card */}
                    {selectedPatient && (
                      <div className="bg-muted/30 p-4 rounded-lg border border-border">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-destructive text-white flex items-center justify-center font-semibold">
                              {selectedPatient.first_name[0]}
                              {selectedPatient.last_name[0]}
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">
                                {selectedPatient.first_name}{" "}
                                {selectedPatient.last_name}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {selectedPatient.phone_number}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-muted-foreground">
                                  Gender: {selectedPatient.gender}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  •
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  Age: {selectedPatient.age} years
                                </span>
                                {selectedPatient.zarity_user_id && (
                                  <>
                                    <span className="text-xs text-muted-foreground">
                                      •
                                    </span>
                                    <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">
                                      {selectedPatient.zarity_user_id}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="link"
                            onClick={handleBackToPatientStep}
                            className="text-primary"
                          >
                            Change Patient
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          Schedule an appointment for the patient{" "}
                          {selectedPatient.first_name}{" "}
                          {selectedPatient.last_name}
                          {mode === "clinic" && selectedClinic
                            ? ` — In-Person at ${selectedClinic.name}`
                            : " — Video Consultation"}
                        </p>
                      </div>
                    )}

                    <div className="border-t" />

                    {/* Staff Member Selection */}
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-2">
                        Select Staff Member
                      </h3>

                      {/* Search Bar */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                        <Input
                          placeholder="Select Member"
                          value={doctorSearchQuery}
                          onChange={(e) => setDoctorSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>

                      {/* Doctor List */}
                      {isLoadingDoctors ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {getFilteredDoctors().map((doctor) => (
                            <button
                              key={doctor.doctor_id}
                              type="button"
                              onClick={() =>
                                handleDoctorSelect(doctor.doctor_id)
                              }
                              className={cn(
                                "w-full p-4 border-2 rounded-lg transition-all hover:shadow-md text-left",
                                selectedDoctor === doctor.doctor_id
                                  ? "border-primary bg-primary/10"
                                  : "border-border hover:border-border"
                              )}
                            >
                              <div className="flex items-start gap-4">
                                {doctor.doctor_profile_pic ? (
                                  <img
                                    src={doctor.doctor_profile_pic}
                                    alt={doctor.doctor_name}
                                    className="w-12 h-12 rounded-full object-cover shrink-0"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold shrink-0">
                                    {doctor.doctor_name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-foreground">
                                    {doctor.doctor_name}
                                  </h3>
                                  <p className="text-sm text-muted-foreground">
                                    {doctor.doctor_designation}
                                  </p>
                                  <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                                    <span>
                                      {doctor.doctor_experience} years exp
                                    </span>
                                    <span>•</span>
                                    <span>₹{doctor.service_cost}</span>
                                    <span>•</span>
                                    <span>
                                      {doctor.service_event_duration} min
                                    </span>
                                  </div>
                                  {doctor.next_available_slot && (
                                    <div className="mt-2 text-xs text-success bg-success/10 inline-block px-2 py-1 rounded">
                                      Next available:{" "}
                                      {doctor.next_available_slot.date} at{" "}
                                      {doctor.next_available_slot.slot}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                          {getFilteredDoctors().length === 0 && (
                            <div className="text-center py-12 text-muted-foreground">
                              No staff members found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : currentStep === "datetime" ? (
                  <>
                    {/* Date and Time Selection Step */}
                    {/* Patient Info Card */}
                    {selectedPatient && (
                      <div className="bg-muted/30 p-4 rounded-lg border border-border">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-destructive text-white flex items-center justify-center font-semibold">
                              {selectedPatient.first_name[0]}
                              {selectedPatient.last_name[0]}
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">
                                {selectedPatient.first_name}{" "}
                                {selectedPatient.last_name}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {selectedPatient.phone_number}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                {selectedPatient.zarity_user_id && (
                                  <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">
                                    {selectedPatient.zarity_user_id}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="link"
                            onClick={handleBackToPatientStep}
                            className="text-primary"
                          >
                            Change Patient
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                          {mode === "clinic" ? (
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <Video className="h-3.5 w-3.5 shrink-0" />
                          )}
                          {mode === "clinic" && selectedClinic
                            ? `In-Person Consultation at ${selectedClinic.name}`
                            : "Video Consultation"}
                        </p>
                      </div>
                    )}

                    <div className="border-t" />

                    {/* Date Selection */}
                    <FormField
                      control={form.control}
                      name="appointmentDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            Select Date
                          </FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "w-full justify-start text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={handleDateSelect}
                                disabled={(date) => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  return date < today;
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Time Slot Selection */}
                    {selectedDate && (
                      <FormField
                        control={form.control}
                        name="timeSlot"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Select Time Slot
                            </FormLabel>
                            <FormControl>
                              {isLoadingSlots ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                  <span className="ml-2 text-sm text-muted-foreground">
                                    Loading available slots...
                                  </span>
                                </div>
                              ) : availableSlots.length > 0 ? (
                                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-80 overflow-y-auto p-1">
                                  {availableSlots.map((slot) => (
                                    <button
                                      key={slot}
                                      type="button"
                                      onClick={() => handleSlotSelect(slot)}
                                      className={cn(
                                        "px-3 py-2 text-sm border-2 rounded-lg transition-all hover:shadow-sm",
                                        selectedSlot === slot
                                          ? "border-primary bg-primary/10 text-primary font-semibold"
                                          : "border-border hover:border-border"
                                      )}
                                    >
                                      {slot}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                  No available slots for this date
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* Part of Program Checkbox */}
                    {selectedSlot && (
                      <div className="space-y-3">
                        <FormField
                          control={form.control}
                          name="promoCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Promo code</FormLabel>
                              <div className="space-y-3 rounded-xl border border-border/60 bg-muted/15 p-4">
                                <div className="flex gap-2">
                                  <FormControl>
                                    <Input
                                      {...field}
                                      autoComplete="off"
                                      disabled={partOfProgram}
                                      placeholder="Optional promo code"
                                      onChange={(event) => {
                                        field.onChange(event);
                                        setPromoQuote(null);
                                        form.clearErrors("promoCode");
                                      }}
                                    />
                                  </FormControl>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    disabled={
                                      partOfProgram ||
                                      !normalizedPromoCode ||
                                      isApplyingPromo
                                    }
                                    onClick={() =>
                                      void loadCheckoutQuote(
                                        normalizedPromoCode
                                      )
                                    }
                                  >
                                    {isApplyingPromo ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Tag className="mr-2 h-4 w-4" />
                                    )}
                                    Apply
                                  </Button>
                                  {normalizedPromoCode && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      onClick={() => {
                                        form.setValue("promoCode", "");
                                        setPromoQuote(null);
                                        form.clearErrors("promoCode");
                                        if (!baseQuote) {
                                          void loadCheckoutQuote("");
                                        }
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </div>
                                {displayedQuote && (
                                  <div className="space-y-2 border-t border-border/60 pt-3 text-sm">
                                    <div className="flex justify-between text-muted-foreground">
                                      <span>Consultation fee</span>
                                      <span>
                                        ₹
                                        {(
                                          displayedQuote.subtotal_paise / 100
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                    {partOfProgram && (
                                      <div className="flex justify-between text-success">
                                        <span>Program coverage</span>
                                        <span>
                                          -₹
                                          {(
                                            displayedQuote.subtotal_paise / 100
                                          ).toFixed(2)}
                                        </span>
                                      </div>
                                    )}
                                    {!partOfProgram &&
                                      displayedQuote.discount_paise > 0 && (
                                        <div className="flex justify-between text-success">
                                          <span>
                                            Promo {displayedQuote.promo_code}
                                          </span>
                                          <span>
                                            -₹
                                            {(
                                              displayedQuote.discount_paise /
                                              100
                                            ).toFixed(2)}
                                          </span>
                                        </div>
                                      )}
                                    <div className="flex justify-between border-t border-border/60 pt-2 font-semibold text-foreground">
                                      <span>Amount to collect</span>
                                      <span>
                                        ₹
                                        {(
                                          displayedQuote.payable_paise / 100
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      Final eligibility and amount are confirmed
                                      when the appointment is scheduled.
                                    </p>
                                  </div>
                                )}
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="partOfProgram"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                    if (checked) {
                                      form.setValue("promoCode", "");
                                      setPromoQuote(null);
                                      form.clearErrors("promoCode");
                                    } else if (!baseQuote) {
                                      void loadCheckoutQuote("");
                                    }
                                  }}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>Part of program</FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    {/* Schedule Button */}
                    {selectedSlot && (
                      <div className="flex justify-end pt-4">
                        <Button
                          type="submit"
                          disabled={
                            isSubmitting || isApplyingPromo || promoNeedsApply
                          }
                          className="bg-primary hover:bg-primary/90"
                        >
                          {isSubmitting && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Schedule Appointment
                        </Button>
                      </div>
                    )}
                  </>
                ) : null}
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
      <AlertDialog
        open={Boolean(checkoutBlockingError)}
        onOpenChange={(open) => {
          if (!open) setCheckoutBlockingError(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promo code not available</AlertDialogTitle>
            <AlertDialogDescription>
              {checkoutBlockingError} The appointment was not created. Apply
              another promo code or remove it before trying again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCheckoutBlockingError(null)}>
              Review promo code
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AddAppointmentForm;
